import { DEFAULT_URLS, INT_CHANNEL_ID, INT_EMBED_ID, PROD_CHANNEL_ID, PROD_EMBED_ID } from '@preview/constants';
import {
  createChannelMock,
  createPlaylistMock,
  createSiteMock,
  createTokenResponseMock,
  createVideoMock,
} from '@preview/helpers/apiHelpers';
import type { MockData } from '../../types';

export const getDefaultMocks = (resiStorageKey: string): MockData => {
  const isInt = resiStorageKey.includes('int');
  const embedId = isInt ? INT_EMBED_ID : PROD_EMBED_ID;
  const channelId = isInt ? INT_CHANNEL_ID : PROD_CHANNEL_ID;

  const video1 = createVideoMock({
    id: '101',
    description: 'Sample Video 1',
    data: {
      id: '101',
      contentUrls: DEFAULT_URLS,
      title: 'Introduction to RESI',
      description: 'Learn about RESI and its features',
      formattedDescription: '**Learn about RESI and its features**',
      thumbnail: {
        url: 'https://placehold.co/300x200?text=RESI+Intro',
      },
      embedId,
      airDate: '2023-01-15T12:00:00Z',
      tags: ['intro', 'tutorial', 'beginner'],
      playlists: [],
    },
  });

  const video2 = createVideoMock({
    id: '102',
    description: 'Sample Video 2',
    data: {
      id: '102',
      contentUrls: DEFAULT_URLS,
      title: 'Advanced Streaming Techniques',
      description: 'Master the art of streaming with RESI',
      formattedDescription: '**Master the art of streaming with RESI**',
      thumbnail: { url: 'https://placehold.co/300x200?text=Advanced+Streaming' },
      embedId,
      airDate: '2023-01-15T12:00:00Z',
      tags: ['intro', 'tutorial', 'beginner'],
      playlists: [],
    },
  });

  const video3 = createVideoMock({
    id: '103',
    description: 'Sample Video 3',
    data: {
      id: '103',
      contentUrls: DEFAULT_URLS,
      title: 'Expert Streaming Techniques',
      description: 'Take your streaming skills to the next level with RESI',
      formattedDescription: '**Take your streaming skills to the next level with RESI**',
      thumbnail: { url: 'https://placehold.co/300x200?text=Expert+Streaming' },
      embedId,
      airDate: '2023-01-15T12:00:00Z',
      tags: ['intro', 'tutorial', 'beginner'],
      playlists: [],
    },
  });

  const video4 = createVideoMock({
    id: '104',
    description: 'Sample Video 4',
    data: {
      id: '104',
      contentUrls: DEFAULT_URLS,
      title: 'Professional Streaming Techniques',
      description: 'Enhance your streaming skills to the next level with RESI',
      formattedDescription: '**Enhance your streaming skills to the next level with RESI**',
      thumbnail: { url: 'https://placehold.co/300x200?text=Professional+Streaming' },
      embedId,
      airDate: '2023-01-15T12:00:00Z',
      tags: ['intro', 'tutorial', 'beginner'],
      playlists: [],
    },
  });

  const video5 = createVideoMock({
    id: '105',
    description: 'Sample Video 5',
    data: {
      id: '105',
      contentUrls: DEFAULT_URLS,
      title: 'Live Streaming Techniques',
      description: 'Make your next live stream with RESI',
      formattedDescription: '**Make your next live stream with RESI**',
      thumbnail: { url: 'https://placehold.co/300x200?text=Live+Streaming' },
      embedId,
      airDate: '2023-01-15T12:00:00Z',
      tags: ['intro', 'tutorial', 'beginner'],
      playlists: [],
    },
  });

  const playlist1 = createPlaylistMock({
    id: 'mp1',
    description: 'Sample Playlist',
    data: {
      id: 'mp1',
      name: 'Playlist 1',
      videos: [video1.data, video2.data, video3.data, video4.data, video5.data],
    },
  });

  const playlist2 = createPlaylistMock({
    id: 'mp2',
    description: 'Sample Playlist 2',
    data: {
      id: 'mp2',
      name: 'Playlist 2',
      videos: [video1.data, video2.data, video3.data, video4.data, video5.data],
    },
  });

  const playlist3 = createPlaylistMock({
    id: 'mp3',
    description: 'Sample Playlist 3',
    data: {
      id: 'mp3',
      name: 'Playlist 3',
      videos: [video1.data, video2.data, video3.data, video4.data, video5.data],
    },
  });

  return {
    mock_playlist_1: playlist1,
    mock_playlist_2: playlist2,
    mock_playlist_3: playlist3,
    mock_video_1: video1,
    mock_video_2: video2,
    mock_video_3: video3,
    mock_video_4: video4,
    mock_video_5: video5,
    mock_site_token: createTokenResponseMock({
      id: 'mock_site_token',
      description: 'Mock Site Token',
      data: {
        jwt: `${btoa(JSON.stringify('mock'))}.${btoa(JSON.stringify('token'))}`,
      },
    }),
    mock_site_feed: createSiteMock({
      id: 'mock_site_feed',
      description: 'Mock Site',
      data: {
        id: 'site-123',
        channelId,
        customerId: 'customer-456',
        name: 'RESI Demo Site',
        description: 'A demo site for RESI on-demand content',
        url: 'https://demo.resi.io',
        branding: {
          mode: 'light',
        },
        googleAnalyticsId: 'UA-12345678-1',
        feeds: [
          {
            id: 'feed-1',
            name: 'Main Feed',
            description: 'Primary content feed',
            default: true,
            playlists: [
              {
                id: playlist1.id,
                name: playlist1.data.name,
                size: playlist1.data.videos.length,
                url: `/api/playlists/${playlist1.id}`,
              },
              {
                id: playlist2.id,
                name: playlist2.data.name,
                size: playlist2.data.videos.length,
                url: `/api/playlists/${playlist2.id}`,
              },
              {
                id: playlist3.id,
                name: playlist3.data.name,
                size: playlist3.data.videos.length,
                url: `/api/playlists/${playlist3.id}`,
              },
            ],
          },
        ],
      },
    }),
    mock_channels_no_event: createChannelMock({
      id: 'mock_channels_no_event',
      description: 'Channels: No Live Event',
      data: {
        channelId,
        channelName: 'RESI Demo Channel',
        live: false,
        nextLive: null,
      },
    }),
    mock_channels_live_event: createChannelMock({
      id: 'mock_channels_live_event',
      description: 'Channels: Live Event Active',
      data: {
        channelId,
        channelName: 'RESI Demo Channel',
        live: true,
        nextLive: {
          name: 'Sunday Service',
          description: 'Join us for our weekly live service',
          startTime: new Date().toISOString(),
          earliestLiveTimeEstimate: new Date().toISOString(),
        },
      },
    }),
  };
};

export const defaultMockConfig = {
  enabled: true,
  forceLoading: false,
  loadingDuration: 2000,
  delay: 500,
  failureRate: 0,
  statusCode: 500,
  errorMessage: 'Mock server error',
};
